import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { KeyedMutator } from 'swr';
import { CleanUser } from '../../export-types/cleaned-types';
import { ModalProps } from '../../services/modals.service';
import { InputWrapper } from './input-wrapper';
import { Company, InnSelect } from './inn-select';
import classNames from 'classnames';
import { innValidation, ogrnValidation } from '../../utils/validations';
import Toggle from 'rsuite/Toggle';
import { api } from '../../services/api';
import { AlertService } from '../../services/alert.service';

type DataType = {
  type: 'entrepreneur' | 'company';
  inn: string;
  name: string;
  ogrn: string;
  address: string;
  kpp?: string;
  management?: string;
};

type Form = {
  type: 'entrepreneur' | 'company';
  inn: string;
  name: string;
  ogrn: string;
  address: string;
  kpp?: string;
  management?: string;
};

interface Props extends ModalProps {
  user: CleanUser;
  mutate?: KeyedMutator<any>;
}

export const CreateUserAccountModal = ({ user, close, mutate }: Props) => {
  const intl = useIntl();

  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
    watch,
    control,
    setValue,
  } = useForm<Form>({
    defaultValues: {
      type: 'company',
    },
  });
  const [inn, type] = watch(['inn', 'type']);
  const [isManual, setIsManual] = React.useState(false);

  const onSelectCompany = (company: DataType) => {
    if (company.type !== 'entrepreneur') {
      reset({
        inn: company.inn,
        name: company.name,
        ogrn: company.ogrn,
        address: company.address,
        type: company.type,
        kpp: company.kpp,
        management: company.management,
      });
    } else {
      setValue('inn', company.inn);
      reset({
        inn: company.inn,
        name: company.name,
        ogrn: company.ogrn,
        type: company.type,
        address: company.address,
      });
    }
  };

  const onSubmit = handleSubmit(async function (form: Form) {
    await api
      .post(`/accounts/user/${user.internalId}`, form)
      .then(() => mutate?.())
      .catch((error) => {
        AlertService.error(error?.message);
      });
    AlertService.success(
      intl.formatMessage({
        defaultMessage: 'Добавлен новый плательщик',
        id: 'TopUpOLD.label.newPayerAdded',
        description: 'Информация на странице',
      }),
    );
    close();
  });

  return (
    <div className="modal-dialog">
      <div className="modal-content">
        <div className="modal-header border-bottom-0 p-4">
          <div className={'fw-600 fs-20'}>
            <FormattedMessage
              defaultMessage="Заполните информацию о юридическом лице, от которого будут оплачивать услуги."
              id="AccountAddFormModal.title-label.fillInTheInformationAboutTheLegalEntity..."
              description="Заголовок в модальном окне"
            />
          </div>

          <button type="button" className="btn-close" onClick={() => close()} />
        </div>

        <div className="modal-body pt-2  ">
          <form onSubmit={onSubmit} id="accountAddForm">
            {!inn && !isManual && (
              <Controller
                name="inn"
                control={control}
                rules={{
                  required: intl.formatMessage({
                    defaultMessage: 'Укажите ИНН организации или ИП',
                    id: 'form.label.indicateTheTINOfTheOrganization...',
                    description: 'Пояснение к полю ввода',
                  }),
                }}
                render={({ field, fieldState }) => {
                  return (
                    <InputWrapper
                      required
                      title={intl.formatMessage({
                        defaultMessage: 'ИНН',
                        id: 'AccountAddFormModal.input-label.TIN',
                        description: 'Заголовок поля ввода',
                      })}
                      error={fieldState.error?.message}
                      className="mb-6"
                    >
                      <div>
                        <InnSelect
                          {...field}
                          placeholder={intl.formatMessage({
                            defaultMessage: 'Введите название компании или ИНН',
                            id: 'form.input-label.enterCompanyNameOrTaxIdentificationNumber',
                            description: 'Поле ввода',
                          })}
                          value={
                            field.value
                              ? ({
                                  inn: field.value,
                                  name: field.value,
                                } as Company)
                              : null
                          }
                          onSelectCompany={onSelectCompany}
                        />
                      </div>
                    </InputWrapper>
                  );
                }}
              />
            )}
            {(isManual || inn) && (
              <>
                <Controller
                  name="inn"
                  control={control}
                  rules={{ ...innValidation }}
                  render={({ field, fieldState }) => (
                    <InputWrapper
                      required
                      title={intl.formatMessage({
                        defaultMessage: 'ИНН',
                        id: 'AccountAddFormModal.input-label.TIN',
                        description: 'Заголовок поля ввода',
                      })}
                      error={fieldState.error?.message}
                      className="mb-2"
                    >
                      <input
                        {...field}
                        inputMode="numeric"
                        className={classNames(
                          fieldState.error
                            ? 'border-red-500'
                            : 'border-gray-300',
                          'form-control',
                        )}
                        autoFocus
                        type="text"
                      />
                    </InputWrapper>
                  )}
                />
                <InputWrapper
                  title={intl.formatMessage({
                    defaultMessage: 'Название организации или ИП',
                    id: 'form.input-label.nameOfOrganizationOrIndividualEntrepreneur',
                    description: 'Заголовок поля ввода',
                  })}
                  error={errors.name?.message}
                  className="mb-2"
                  required
                >
                  <input
                    autoComplete="organization"
                    className={classNames(
                      errors.name?.message
                        ? 'border-red-500'
                        : 'border-gray-300',
                      'form-control',
                    )}
                    type="text"
                    {...register('name', {
                      required: intl.formatMessage({
                        defaultMessage: 'Укажите название организации или ИП',
                        id: 'AccountAddFormModal.input-label.indicateTheNameOfTheOrganization...',
                        description: 'Пояснение к полю ввода',
                      }),
                    })}
                  />
                </InputWrapper>
                <Controller
                  name="ogrn"
                  control={control}
                  rules={{ ...ogrnValidation }}
                  render={({ field, fieldState }) => (
                    <InputWrapper
                      title={intl.formatMessage({
                        defaultMessage: 'ОГРН / ОГРНИП',
                        id: 'AccountAddFormModal.input-label.registrationNumber',
                        description: 'Заголовок поля ввода',
                      })}
                      error={fieldState.error?.message}
                      className="mb-2"
                      required
                    >
                      <input
                        {...field}
                        inputMode="numeric"
                        className={classNames(
                          fieldState.error
                            ? 'border-red-500'
                            : 'border-gray-300',
                          'form-control',
                        )}
                        type="text"
                      />
                    </InputWrapper>
                  )}
                />
                <InputWrapper
                  title={intl.formatMessage({
                    defaultMessage: 'Юридический адрес',
                    id: 'AccountAddFormModal.input-label.legalAddress',
                    description: 'Заголовок поля ввода',
                  })}
                  error={errors.address?.message}
                  className="mb-2"
                  required
                >
                  <textarea
                    className={classNames(
                      errors.address?.message
                        ? 'border-red-500'
                        : 'border-gray-300',
                      'form-control',
                    )}
                    {...register('address', {
                      required: intl.formatMessage({
                        defaultMessage: 'Укажите юридический адрес организации',
                        id: 'AccountAddFormModal.input-label.specifyTheLegalAddressOfTheOrganization',
                        description: 'Пояснение к полю ввода',
                      }),
                    })}
                  />
                </InputWrapper>

                {isManual && (
                  <Controller
                    name="type"
                    control={control}
                    render={({ field }) => {
                      return (
                        <div className="flex items-center mb-3">
                          <Toggle
                            checked={field.value === 'entrepreneur'}
                            onChange={(value) => {
                              field.onChange(
                                value ? 'entrepreneur' : 'company',
                              );
                            }}
                          />
                          <div className="ml-2">
                            Индивидуальный предприниматель
                          </div>
                        </div>
                      );
                    }}
                  />
                )}
                {type === 'company' && (
                  <>
                    <InputWrapper
                      title={intl.formatMessage({
                        defaultMessage: 'КПП',
                        id: 'AccountAddFormModal.input-label.registrationReasonCode',
                        description: 'Заголовок поля ввода',
                      })}
                      error={errors.kpp?.message}
                      className="mb-2"
                      required
                    >
                      <input
                        inputMode="numeric"
                        className={classNames(
                          errors.kpp?.message
                            ? 'border-red-500'
                            : 'border-gray-300',
                          'form-control',
                        )}
                        type="number"
                        min={0}
                        step={1}
                        {...register('kpp', {
                          required: intl.formatMessage({
                            defaultMessage: 'Укажите КПП',
                            id: 'AccountAddFormModal.input-label.indicateTheReasonCodeForRegistration',
                            description: 'Пояснение к полю ввода',
                          }),
                          pattern: {
                            value: /^([0-9]{9})?$/,
                            message: intl.formatMessage({
                              defaultMessage: 'КПП должен состоять из 9 цифр',
                              id: 'AccountAddFormModal.input-label.mustConsistOf9Digits',
                              description: 'Пояснение к полю ввода',
                            }),
                          },
                        })}
                      />
                    </InputWrapper>
                    <InputWrapper
                      title={intl.formatMessage({
                        defaultMessage: 'ФИО директора',
                        id: 'AccountAddFormModal.input-label.fullNameOfTheDirector',
                        description: 'Заголовок поля ввода',
                      })}
                      error={errors.management?.message}
                      className="mb-2"
                      required
                    >
                      <input
                        className={classNames(
                          errors.management?.message
                            ? 'border-red-500'
                            : 'border-gray-300',
                          'form-control',
                        )}
                        type="text"
                        {...register('management', {
                          required: intl.formatMessage({
                            defaultMessage: 'Укажите ФИО директора',
                            id: 'AccountAddFormModal.input-label.indicateTheNameOfTheDirector',
                            description: 'Пояснение к полю ввода',
                          }),
                        })}
                      />
                    </InputWrapper>
                  </>
                )}
              </>
            )}
          </form>
          {(inn || isManual) && (
            <div className=" px-6 mt-4 ">
              <div className="w-full">
                <button
                  type="button"
                  className={'btn btn-success'}
                  onClick={() => onSubmit()}
                >
                  Добавить
                </button>
              </div>
            </div>
          )}
          {!isManual && !inn && (
            <button
              type="button"
              className={classNames(
                isManual && 'hidden',
                'btn btn-secondary mt-4',
              )}
              onClick={() => setIsManual(true)}
            >
              <div className="truncate">
                <FormattedMessage
                  id="AccountAddFormModal.input-label.fillManually"
                  defaultMessage="Заполнить вручную"
                  description="Заголовок поля ввода"
                />
              </div>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
